import { useQuery } from '@tanstack/react-query';
import { UcrBar3, UcrPie1, UcrShield2 } from '@ucr/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { MEDICAL_DEVICES_FEATURE } from 'app/providers/featureFlags';

import { getMedOrgName } from 'entities/MegOrgs';
import { usePermissions } from 'entities/Role/lib/hooks';
import { isRoleKey } from 'entities/Role/types';
import { TUser } from 'entities/User';
import { getCurrentUser } from 'entities/User/lib/utils';

import { QueryKeysFactory, apiClient } from 'shared/api';

import { MenuItem } from './menu.types';

const MANAGE_PATH = '/manage';
const STATS_PATH = '/stats';

export const useMenu = (currentUser: TUser) => {
  const { pathname } = useLocation();

  const { hasPermission } = usePermissions(
    isRoleKey(currentUser.role.key) ? currentUser.role.key : undefined,
  );

  /** Проверка на возможность управления медорганизациями */
  const canManageMedicalOrgs = hasPermission('medorgs.update');

  const canQueryDataLensList = hasPermission('datalens.reports.list');

  const isErpAdmin = getCurrentUser()!.role.key === 'admin.erp';

  /** Медорганизация текущего пользователя */
  const currentUserMedOrg = useMemo(() => {
    return currentUser.medorg;
  }, [currentUser]);

  const manageItems = useMemo<MenuItem[]>(() => {
    return [
      canManageMedicalOrgs
        ? { title: 'Медорганизации', link: `${MANAGE_PATH}/medical_orgs` }
        : null,
      currentUserMedOrg
        ? {
            title: getMedOrgName(currentUserMedOrg) || 'Медорганизация',
            link: `${MANAGE_PATH}/medical_orgs/${currentUserMedOrg.id}`,
          }
        : null,
      {
        title: 'Типы медицинских изделий',
        link: `${MANAGE_PATH}/medical_devices_types`,
      },
      MEDICAL_DEVICES_FEATURE.canUse
        ? {
            title: 'Медицинские изделия',
            link: '/manage/medical_devices',
          }
        : null,
      { title: 'Пользователи', link: '/manage/users' },
      isErpAdmin
        ? {
            title: 'Производители',
            link: '/manage/medical_device_manufacturers',
          }
        : null,
    ].filter((item): item is MenuItem => Boolean(item));
  }, [canManageMedicalOrgs, currentUserMedOrg, isErpAdmin]);

  const { data } = useQuery({
    queryKey: QueryKeysFactory.GetDataLensControllerWebListKey(),
    enabled: canQueryDataLensList,
    queryFn: () => apiClient.datalensControllerWebList(),
  });

  const menu = useMemo(() => {
    return [
      {
        title: 'Управление',
        icon: UcrShield2,
        children: manageItems,
        disabled: false,
      },
      {
        title: 'Статистика',
        icon: UcrBar3,
        children:
          data?.reports.map((report) => ({
            title: report.title,
            link: `/stats/${report.sourceId}`,
          })) ?? [],
        disabled: false,
      },
      {
        title: 'Отчёты',
        icon: UcrPie1,
        children: [],
        disabled: true,
      },
    ];
  }, [data?.reports, manageItems]);

  const [currentGroup, setCurrentGroup] = useState<string>(menu[0].title);

  const _setCurrentGroup = useCallback((group: string) => {
    setCurrentGroup(group);
  }, []);

  useEffect(() => {
    if (pathname.startsWith(MANAGE_PATH)) {
      _setCurrentGroup(menu[0].title);
    } else if (pathname.startsWith(STATS_PATH)) {
      _setCurrentGroup(menu[1].title);
    } else {
      // default
      _setCurrentGroup(menu[0].title);
    }
  }, [pathname, _setCurrentGroup]);

  return { menu, currentGroup, setCurrentGroup };
};
